import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createCategory,
  getAllCategory,
  deleteCategory,
  updateCategory,
  createCategoryBanner,
  updateCategoryBanner
} from "../../State/Category/Action";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Box,
  Tooltip,
  CardContent,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import { Add, AddCircle, Close, Delete, Edit, FileDownload, PictureAsPdf, Print, UploadFile } from "@mui/icons-material";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";
import { getVendors } from "../../State/Vendor/Action";
import { getProducts } from "../../State/Product/Action";
import ImagePopup from "../../../Common/ImagePopup";

const AddCategory = () => {
  const dispatch = useDispatch();
  const { adminCategories, adminVendor, adminProduct } = useSelector((store) => store);

  const [categoryData, setCategoryData] = useState({ categoryName: "" });
  const [editingId, setEditingId] = useState(null);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [error, setError] = useState("");
  const [openFormModal, setOpenFormModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bannerImage, setBannerImage] = useState(null);
  const [uploadedBannerImage, setUploadedBannerImage] = useState(null);
  const [bannerImageFile, setBannerImageFile] = useState(null);
  const [bannerEditingId, setBannerEditingId] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    dispatch(getAllCategory());
    dispatch(getProducts());
    dispatch(getVendors());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPG, JPEG, and PNG images are allowed!");
      setSelectedImageFile(null);
      setImagePreview(null);
      return;
    }
    // ✅ File size check (max 2MB)
    if (file?.size > 2 * 1024 * 1024) {
      setError("Only less then 2MB size image are allowed!");
      setSelectedImageFile(null);
      setImagePreview(null);
      return;
    }

    // ✅ Quality check
    checkImageQuality(file);
  };

  const checkImageQuality = (file) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      // ✅ Check: Image must be square
      if (width !== height) {
        setError("Only square image are allowed (width and height must be equal)!");
        setSelectedImageFile(null);
        setImagePreview(null);
        return;
      }

      // ✅ Create canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // ✅ Convert to grayscale
      const gray = new Uint8ClampedArray(width * height);
      for (let i = 0, j = 0; i < data.length; i += 4, j++) {
        gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }

      // ✅ Compute Laplacian variance
      const kernel = [
        [0, 1, 0],
        [1, -4, 1],
        [0, 1, 0],
      ];

      const response = [];
      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          let sum = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const pixel = gray[(y + ky) * width + (x + kx)];
              sum += kernel[ky + 1][kx + 1] * pixel;
            }
          }
          response.push(sum);
        }
      }

      const mean = response.reduce((a, b) => a + b, 0) / response.length;
      const variance =
        response.reduce((a, b) => a + (b - mean) ** 2, 0) / response.length;

      console.log("📸 Image variance:", variance.toFixed(2));

      // ✅ Adjust threshold dynamically
      // Try starting from 20 → if still says blurry, reduce to 10 or 5
      if (variance < 50) {
        setError("Blur image are not allowed!");
        setSelectedImageFile(null);
        setImagePreview(null);
        return;
      }

      setError("");
      setSelectedImageFile(file);
      setImagePreview(URL.createObjectURL(file));

      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    };

  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (categoryData.categoryName.trim() === "") {
      toast.error("Category Name is required!");
      return;
    }

    if (categoryData.categoryName.length < 3 || categoryData.categoryName.length > 50) {
      toast.error("Category name should be between 3 to 50 characters.");
      return;
    }

    const formData = new FormData();
    if (selectedImageFile) {
      formData.append("imageFile", selectedImageFile);
    }

    if (editingId) {
      dispatch(updateCategory(editingId, categoryData, formData));
    } else {
      if (!selectedImageFile) {
        setError("Please upload an image.");
        return;
      }
      dispatch(createCategory(categoryData, formData));
    }

    handleCancle();
  };

  const handleCategoryUpdate = (item) => {
    setCategoryData({ categoryName: item.name });
    setUploadedImage(item.image);
    setSelectedImageFile(null);
    setImagePreview(null);
    setEditingId(item.id);

    setOpenFormModal(true);
  };

  const confirmDelete = (id) => setDeleteDialog({ open: true, id });

  const handleDeleteConfirmed = () => {
    dispatch(deleteCategory(deleteDialog.id));
    setDeleteDialog({ open: false, id: null });
  };

  const handleCancle = () => {
    setCategoryData({
      categoryName: ""
    });
    setSelectedImageFile(null);
    setImagePreview(null);
    setUploadedImage(null);
    setEditingId(null);
    setOpenFormModal(false);
  }

  const handleDeleteCancel = () => setDeleteDialog({ open: false, id: null });

  // Handle plus click
  const handleAddClick = (row) => {
    setSelectedCategory(row);
    setOpenPopup(true);
    setSelectedVendors([]);
    setSelectedProducts([]);
    setSelectedProductIds([]);
    setBannerImage(null);
    setBannerImageFile(null);
    setUploadedBannerImage(null)
    setBannerEditingId(null)
  };


  // Vendor checkbox toggle
  const handleVendorChange = (vendorId) => {
    if (selectedVendors.includes(vendorId)) {
      // ✅ Uncheck vendor → remove vendor + उसके सारे products हटाओ
      setSelectedVendors(selectedVendors.filter((id) => id !== vendorId));

      setSelectedProducts(
        selectedProducts.filter(
          (p) => p.vendor.id !== vendorId
        )
      );
      setSelectedProductIds(
        selectedProducts
          .filter((p) => p.vendor.id !== vendorId) // पहले unwanted products हटा दो
          .map((p) => p.id) // फिर सिर्फ product.id लो
      );

    } else {
      setSelectedVendors([...selectedVendors, vendorId]);
    }
  };

  // Product checkbox toggle
  const handleProductChange = (product) => {
    if (selectedProducts.includes(product) || selectedProductIds.includes(product.id)) {
      setSelectedProducts(selectedProducts.filter((p) => p !== product));
      setSelectedProductIds(selectedProductIds.filter((id) => id !== product.id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
      setSelectedProductIds([...selectedProductIds, product.id]);
    }
  };

  // Image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerImageFile(file);
    setBannerImage(URL.createObjectURL(file));
  };

  const handleSave = () => {

    // Validation: At least one vendor or one product
    if (selectedVendors.length === 0 && selectedProductIds.length === 0) {
      toast.error("Please select at least one Vendor or one Product!");
      return;
    }

    // Payload
    const payload = {
      categoryId: selectedCategory.id,  // category selected होना चाहिए
      vendorIds: selectedVendors,    // vendors array
      productIds: selectedProductIds,  // products array
    };

    const formData = new FormData();
    if (bannerImageFile) {
      formData.append("imageFile", bannerImageFile);
    }

    console.log("Final Payload:", payload);
    if (bannerEditingId) {
      dispatch(updateCategoryBanner(bannerEditingId, payload, formData));
    } else {
      if (!bannerImageFile) {
        toast.error("Banner image is required!");
        return;
      }
      dispatch(createCategoryBanner(payload, formData))
    }
    setOpenPopup(false)
    setSelectedVendors([]);
    setSelectedProducts([]);
    setSelectedProductIds([]);
    setBannerImage(null);
    setBannerImageFile(null);
    setUploadedBannerImage(null)
    setBannerEditingId(null)
  };

  const handleEditClick = (row) => {
    console.log("upd", row)
    setSelectedCategory(row);
    setBannerEditingId(row.banner.id)
    setOpenPopup(true);
    setSelectedVendors(row.banner.vendors.map(v => v.id));
    setSelectedProducts(row.banner.products);
    setSelectedProductIds(row.banner.products.map(p => p.id));
    setBannerImageFile(row.bannerImage);
    setUploadedBannerImage(row.bannerImage)
    setBannerImage(null);
  };

  const writeAction = PermissionService.has({ category: "Admin", page: "Categories", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "name", headerName: "Category Name", flex: 1 },
    {
      field: "image",
      headerName: "Image",
      flex: 1,
      renderCell: (params) => (
        <img onClick={() => { setImageOpen(true); setImageUrl(params?.value) }} className="cursor-pointer" src={params.value} alt="category" width="30" height="30" style={{ borderRadius: 8 }} />
      )
    },
    {
      field: "bannerImage",
      headerName: "Banner",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          {writeAction && (
            <>
              {params.row.banner ? (
                <Tooltip title="Edit Banner">
                  <Edit
                    fontSize="small"
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleEditClick(params.row)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Add Banner">
                  <AddCircle
                    fontSize="small"
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleAddClick(params.row)}
                  />
                </Tooltip>
              )}
            </>
          )}
          {/* Primary image - slightly bigger */}
          {params.value && (
            <Box
              onClick={() => { setImageOpen(true); setImageUrl(params?.value) }}
              component="img"
              src={params.value}
              alt="Category Banner"
              sx={{
                width: 80,
                height: 30,
                borderRadius: 2,
                cursor: "pointer"
              }}
            />
          )}

        </Box>
      ),
    },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleCategoryUpdate(params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => confirmDelete(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    ] : []),
  ];


  const rows = adminCategories?.categories?.response?.map((cat, index) => ({
    id: cat.id,
    name: cat.name,
    image: cat.image,
    bannerImage: cat?.banner?.imageUrl,
    banner: cat?.banner
  })) || [];

  const filteredRows = rows?.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(query.toLowerCase())
    );
  });

    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1, row.name].join(',')).join('\n');
    const headers = ['S.No.', 'Category Name'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'categories.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Category List", 14, 15);
    const tableColumn = ['S.No.', 'Category Name'];
    const tableRows = exportRows.map((row, index) => [index + 1, row.name]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('categories.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Category Name'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.name]);
    const html = `
          <html>
            <head>
              <title>Category List</title>
              <style>
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid black; padding: 8px; text-align: left; }
                th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;}
                tr:nth-child(even) { background-color: #f5f5f5; }
              </style>
            </head>
            <body>
              <h2>Category List</h2>
              <table>
                <thead>
                  <tr>
                    ${headers.map(header => `<th>${header}</th>`).join('')}
                  </tr>
                </thead>
                <tbody>
                  ${tableContent.map(row => `
                    <tr>
                      ${row.map(cell => `<td>${cell}</td>`).join('')}
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </body>
          </html>
        `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div >

      <Card className="p-3" sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6" className="text-gray-700">CATEGORIES</Typography>}
            sx={{ p: 0 }}
          />
          {writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenFormModal(true);
                  setEditingId(null);
                }}
              >
                <Add />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                ADD CATEGORY
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
          </Box>
          <Box>
            <TextField
              label="Search Categories"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
          </Box>
        </Box>

        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            getRowId={(row) => row.id}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={adminCategories.loading}
            autoHeight={true}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                padding: "8px",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this category?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace the form rendering with a Dialog: */}
      <Dialog
        open={openFormModal}
        onClose={handleCancle}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {editingId ? "UPDATE" : "ADD"} CATEGORY
          <IconButton onClick={handleCancle} size="small" sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Category Name"
                      name="categoryName"
                      value={categoryData.categoryName}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ py: 1.8 }}
                        startIcon={<UploadFile />}
                      >
                        Upload Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </Button>

                    </Box>

                    {error && (
                      <Typography color="error" variant="caption" mt={1}>
                        {error}
                      </Typography>
                    )}
                  </Grid>

                  {uploadedImage && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">Current Image:</Typography>
                      <img src={uploadedImage} alt="Uploaded" width="60" height="60" style={{ borderRadius: 8 }} />
                    </Grid>
                  )}
                  {imagePreview && (
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">Image Preview:</Typography>
                      <img src={imagePreview} alt="Preview" width="60" height="60" style={{ borderRadius: 8 }} />
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                          boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                          transform: "scale(1.02)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      {editingId ? "Update " : "Add "} Category
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancle}
                      sx={{
                        color: "#449AEF",
                        borderColor: "#449AEF",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#449AEF",
                          color: "#fff",
                          borderColor: "#449AEF",
                          boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

      {/* Popup Dialog */}

      <Dialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh", // keep height manageable
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "1.25rem",
            fontWeight: "bold",
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          {bannerEditingId ? "UPDATE" : "ADD"} CATEGORY BANNER
          <IconButton onClick={() => setOpenPopup(false)} size="small" sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Scrollable Content */}
        <DialogContent
          sx={{
            px: 2,
            py: 1,
            flex: 1,
            overflowY: "auto",
          }}
        >
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={3}>
                {/* Category & Upload */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Category"
                    value={selectedCategory?.name || ""}
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={2} size="small">
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{
                        py: 1,
                        fontSize: "0.8125rem", // matches MUI's small size
                        textTransform: "none",  // optional: prevent uppercase if undesired
                        minHeight: "36px",      // height for small buttons
                      }}
                      startIcon={<UploadFile fontSize="small" />} // smaller icon
                    >
                      Upload Banner Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                      />
                    </Button>
                  </Box>
                </Grid>

                {/* Preview */}
                {bannerImage && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">Image Preview:</Typography>
                    <img
                      src={bannerImage}
                      alt="Uploaded"
                      width="150"
                      height="150"
                      style={{ borderRadius: 8 }}
                    />
                  </Grid>
                )}
                {/* Preview */}
                {uploadedBannerImage && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">Current Image:</Typography>
                    <img
                      src={uploadedBannerImage}
                      alt="Preview"
                      width="150"
                      height="150"
                      style={{ borderRadius: 8 }}
                    />
                  </Grid>
                )}
              </Grid>

              {/* Vendors */}
              <Typography variant="h6" mt={1} mb={1}>
                Vendors
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)" // ✅ ek row me 3 vendors
                gap={2}
                mb={2}
              >
                {adminVendor?.vendors?.response
                  ?.filter((v) => v?.approved && !v?.isBlocked)
                  ?.map((vendor) => (
                    <Box
                      key={vendor.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        px: 1,
                        py: 1,
                        boxShadow: 1,
                        transition: "all 0.2s",
                        "&:hover": { boxShadow: 3, borderColor: "primary.main" },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small" // ✅ chhota checkbox
                            checked={selectedVendors.includes(vendor.id)}
                            onChange={() => handleVendorChange(vendor.id)}
                          />
                        }
                        label={vendor.name}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.8rem", // ✅ font size chhota
                          },
                        }}
                      />
                    </Box>
                  ))}
              </Box>

              {/* Products */}
              <Typography variant="h6" mb={1}>
                Products
              </Typography>
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                gap={2}
              >
                {(() => {
                  const productsToShow =
                    selectedVendors.length === 0
                      ? adminProduct.products?.response || []
                      : adminProduct.products?.response?.filter((p) =>
                        selectedVendors.includes(p.vendor.id)
                      ) || [];

                  return productsToShow?.map((product) => (
                    <Box
                      key={product.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        px: 1,
                        py: 1,
                        boxShadow: 1,
                        transition: "all 0.2s",
                        "&:hover": { boxShadow: 3, borderColor: "primary.main" },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small" // ✅ chhota checkbox
                            checked={selectedProductIds.includes(product.id)}
                            onChange={() => handleProductChange(product)}
                          />
                        }
                        label={product.title}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.8rem", // ✅ font size chhota
                          },
                        }}
                      />
                    </Box>
                  ));
                })()}
              </Box>
            </CardContent>
          </Card>
        </DialogContent>

        {/* Fixed Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid rgba(0,0,0,0.1)",
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" }, // responsive
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "6px",
              px: 2.5,
              py: 0.8,
              transition: "all 0.2s",
              "&:hover": {
                background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                transform: "scale(1.02)",
              },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {bannerEditingId ? "Update" : "Add"} Category Banner
          </Button>
          <Button
            variant="outlined"
            onClick={() => setOpenPopup(false)}
            sx={{
              color: "#449AEF",
              borderColor: "#449AEF",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "6px",
              px: 2.5,
              py: 0.8,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#449AEF",
                color: "#fff",
                borderColor: "#449AEF",
                boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
              },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {/* Common Popup */}
      <ImagePopup
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        imageUrl={imageUrl}
      />
    </div>
  );
};

export default AddCategory;
